import type {
  InternalActionSheetButton,
  InternalActionSheetOptions
} from "./types";
import type {
  Placement,
  VerticalOrigin,
  HorizontalOrigin
} from "./DropdownActionSheet.types";
import { LAYOUT } from "./DropdownActionSheet.constants";

export interface MenuGroup {
  section?: string;
  buttons: InternalActionSheetButton[];
}

/**
 * Groups consecutive buttons that share the same `section` so each run can be
 * wrapped in a MenuSection with a header. Buttons with no section form their
 * own un-headed group.
 */
export const buildMenuGroups = (
  buttons: readonly InternalActionSheetButton[]
): MenuGroup[] => {
  const groups: MenuGroup[] = [];
  for (const button of buttons) {
    const section = button.section;
    const last = groups[groups.length - 1];
    if (last && last.section === section) {
      last.buttons.push(button);
    } else {
      groups.push({ section, buttons: [button] });
    }
  }
  return groups;
};

/**
 * Depth-first flatten of buttons + all descendants (submenu children).
 * Used to look up a leaf id that may be nested several levels deep.
 */
export const flattenButtons = (
  buttons: readonly InternalActionSheetButton[]
): InternalActionSheetButton[] =>
  buttons.flatMap((b) =>
    b.children?.length ? [b, ...flattenButtons(b.children)] : [b]
  );

export interface PlacementResult {
  placement: Placement;
}

export function calculatePlacement(
  triggerPosition: { x: number; y: number },
  options: InternalActionSheetOptions<readonly InternalActionSheetButton[]>
): PlacementResult {
  const estimatedHeaderHeight =
    options.title || options.message ? LAYOUT.HEADER_HEIGHT : 0;
  const estimatedMenuHeight = options.buttons.length * LAYOUT.ITEM_HEIGHT;
  const estimatedTotalHeight = estimatedHeaderHeight + estimatedMenuHeight;

  const spaceBelow = window.innerHeight - triggerPosition.y;
  const spaceAbove = triggerPosition.y;
  const spaceRight = window.innerWidth - triggerPosition.x;
  const spaceLeft = triggerPosition.x;

  const vertical: VerticalOrigin =
    spaceBelow < estimatedTotalHeight + LAYOUT.BOTTOM_PADDING &&
    spaceAbove > spaceBelow
      ? "top"
      : "bottom";

  const horizontal: HorizontalOrigin =
    spaceRight < LAYOUT.MENU_WIDTH + LAYOUT.VIEWPORT_MARGIN &&
    spaceLeft > spaceRight
      ? "right"
      : "left";

  const placement: Placement = `${vertical} ${horizontal === "left" ? "start" : "end"}`;

  return { placement };
}

export function calculateHighlightPosition(
  itemElement: HTMLElement | null,
  menuElement: HTMLElement | null
): { top: number; height: number } | null {
  if (!itemElement || !menuElement) {
    return null;
  }

  const menuRect = menuElement.getBoundingClientRect();
  const itemRect = itemElement.getBoundingClientRect();

  // `menuRect`/`itemRect` are viewport-relative, so their delta is the item's
  // VISIBLE offset within the scroll viewport. FloatingHighlight is absolutely
  // positioned inside that scroll container and scrolls with its content, so it
  // must be placed at the item's offset within the scrolled content — add
  // scrollTop, otherwise the highlight drifts upward once the menu is scrolled.
  let top = itemRect.top - menuRect.top + menuElement.scrollTop;
  let { height } = itemRect;

  if (itemElement.dataset.divider === "true") {
    const style = getComputedStyle(itemElement);
    const offset =
      parseFloat(style.borderTopWidth) +
      parseFloat(style.paddingTop) -
      parseFloat(style.paddingBottom);
    top += offset;
    height -= offset;
  }

  return { top, height };
}
